import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  Card,
  Container,
  Form,
  Header,
  Icon,
  Label,
  List,
  ListItem,
  Message,
  MessageHeader,
  Modal,
  Popup,
  Segment,
} from "semantic-ui-react";
import { disconnectService, loginDynamics365 } from "../api/auth";
import {
  addWhatsappNumber,
  getWhatsappConfig,
  updateWhatsappConfig,
} from "../api/whatsapp";
import CopyableText from "../components/CopyableText";
import PageLoader from "../components/PageLoader";
import { useAuth } from "../contexts/AuthContext";
import dynamics365Logo from "../images/dynamics365-logo.png";
import whatsappLogo from "../images/whatsapp-logo.png";

const apps = [
  {
    integrationName: "dynamics365",
    featureName: "dynamics365",
    name: "Dynamics365",
    description: "Enable contact searching and call loggins with Dynamics365",
    docsUrl: "https://docs.connect.cosoft.co.uk/apps/dynamics365",
    connectButton: <Dynamics365Integration />,
    adminFeature: true,
  },
  {
    integrationName: "whatsapp",
    featureName: "whatsapp",
    name: "WhatsApp",
    description:
      "Configure WhatsApp integration for all users in your organisation who have access to it.",
    docsUrl: "https://docs.connect.cosoft.co.uk/apps/whatsapp/configuring",
    connectButton: <WhatsappIntegration />,
    adminFeature: true,
  },
];

function Apps() {
  const { apiUser, hasFeature, hasIntegration } = useAuth();

  return (
    <Container
      style={{
        margin: "5px 0px",
      }}
    >
      <Segment basic>
        <Header as="h2" style={{ marginBottom: "40px" }}>
          Manage Connect Apps
        </Header>
        <Card.Group>
          {apps
            .filter(
              (x) =>
                (x.featureName === undefined ||
                  hasFeature([x.integrationName]) ||
                  x.alwaysShow) &&
                (!x.adminFeature || apiUser?.is_admin),
            )
            .sort((a, b) =>
              hasIntegration([a.integrationName]) >
              hasIntegration([b.integrationName])
                ? -1
                : 1,
            )
            .map((app) => (
              <AppCard
                key={app.integrationName}
                connected={hasIntegration([app.integrationName])}
                {...app}
              />
            ))}
        </Card.Group>
      </Segment>
    </Container>
  );
}

function AppCard({
  integrationName,
  name,
  description,
  docsUrl,
  connected,
  connectButton,
}) {
  const { setApiUser } = useAuth();
  const [isHovering, setIsHovering] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const res = await disconnectService(integrationName);
      setApiUser(res.data.data);
      toast("Successfully disconnected", { type: "success" });
    } catch (e) {
      toast("Failed to disconnect", { type: "error" });
    }
    setDisconnecting(false);
  };

  return (
    <Card
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      style={{
        scale: isHovering ? "1.05" : "1",
        transition: "all 100ms ease-in-out",
      }}
    >
      <Card.Content>
        <Card.Header>
          {connected && <Icon name="check circle" color="green" />} {name}
        </Card.Header>
        <p style={{ marginTop: "12px" }}>{description}</p>

        {docsUrl && (
          <a href={docsUrl} target="_blank" rel="noreferrer">
            Learn more
          </a>
        )}
      </Card.Content>
      <Card.Content extra>
        <div style={{ marginTop: "8px" }}>
          {connected ? (
            <Button
              color="red"
              onClick={handleDisconnect}
              disabled={disconnecting}
              loading={disconnecting}
            >
              Disconnect
            </Button>
          ) : (
            <>{connectButton}</>
          )}
        </div>
      </Card.Content>
    </Card>
  );
}

function WhatsappIntegration() {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [whatsappBusinessId, setWhatsappBusinessId] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const isFormModified = () => {
    return apiKey !== "" || appSecret !== "" || whatsappBusinessId !== "";
  };

  const loadConfig = async () => {
    setLoading(true);
    const config = await getWhatsappConfig();
    setConfig(config?.data?.data);
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      loadConfig();
    }
  }, [open]);

  const handleSubmit = () => {
    setSubmitting(true);
    updateWhatsappConfig({
      api_key: apiKey,
      app_secret: appSecret,
      whatsapp_business_id: whatsappBusinessId,
    })
      .then(() => {
        toast("Configuration updated", { type: "success" });
        setApiKey("");
        setAppSecret("");
        setWhatsappBusinessId("");
        setError("");
        setShowForm(false);
        loadConfig();
      })
      .catch((res) => setError(res.response.data.message))
      .finally(() => setSubmitting(false));
  };

  const handleAddNumber = (phoneNumberId, phoneNumber) => {
    setSubmitting(true);
    addWhatsappNumber({
      phone_number_id: phoneNumberId,
      phone_number: phoneNumber,
    })
      .then(() => {
        toast("Phone number added", { type: "success" });
        setError("");
        loadConfig();
      })
      .catch((res) => setError(res.response.data.message))
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <Modal
        onClose={() => {
          setShowForm(false);
          setError("");
          setAppSecret("");
          setApiKey("");
          setWhatsappBusinessId("");
          setOpen(false);
        }}
        onOpen={() => setOpen(true)}
        open={open}
        trigger={
          <Label
            image
            size="big"
            as="a"
            color="blue"
            style={{ paddingTop: "0.49em" }}
          >
            <img src={whatsappLogo} alt="WhatsApp logo" />
            Configure WhatsApp
          </Label>
        }
      >
        <Modal.Header>WhatsApp integration</Modal.Header>
        <Modal.Content>
          <Modal.Description>
            {loading ? (
              <PageLoader>Loading...</PageLoader>
            ) : (
              <>
                <Header>Step 1: Set up webhooks</Header>
                <p>
                  If you haven't set up a WhatsApp app in your Meta developer
                  console yet, you should do that first.
                </p>
                <p>
                  The next step is to configure webhooks in the Meta developer
                  console. You'll need to enter these details:
                </p>
                <p>
                  <strong>Callback URL:</strong>{" "}
                  <CopyableText text={config?.callback_url} />
                </p>
                <p>
                  <strong>Verify token:</strong>{" "}
                  <CopyableText text={config?.validation_token} />
                </p>
                <hr />
                <Header>Step 2: Enter API details</Header>
                {config?.is_configured ? (
                  <>
                    <p>
                      <strong>
                        You've already configured WhatsApp. If you change the
                        configuration here it may disconnect your current
                        WhatsApp integration.
                      </strong>
                    </p>
                    <p>
                      You don't need to re-enter all of the details to change
                      the configuration &ndash; just complete the fields you
                      want to change and leave the others blank to retain their
                      previously-configured values.
                    </p>
                  </>
                ) : (
                  <p>
                    Once you've successfully completed step 1, you'll need to
                    get some details from the Meta developer console to enter
                    into Connect.
                  </p>
                )}
                {!config?.is_configured || showForm ? (
                  <Form>
                    {error.length > 0 && (
                      <Message negative>
                        <p>{error}</p>
                      </Message>
                    )}
                    <Form.Field>
                      <label>API Key</label>
                      <input
                        placeholder={
                          config?.is_configured
                            ? "******************"
                            : "Enter an API key"
                        }
                        value={apiKey}
                        onChange={(e) => {
                          setError("");
                          setApiKey(e.target.value.trim());
                        }}
                      />
                    </Form.Field>
                    <Form.Field>
                      <label>App Secret</label>
                      <input
                        placeholder={
                          config?.is_configured
                            ? "************"
                            : "Enter an App secret"
                        }
                        value={appSecret}
                        onChange={(e) => {
                          setError("");
                          setAppSecret(e.target.value.trim());
                        }}
                      />
                    </Form.Field>
                    <Form.Field>
                      <label>WhatsApp Business ID</label>
                      <input
                        placeholder={
                          config?.is_configured
                            ? "********"
                            : "Enter a WhatsApp Business ID"
                        }
                        value={whatsappBusinessId}
                        onChange={(e) => {
                          setError("");
                          setWhatsappBusinessId(e.target.value.trim());
                        }}
                      />
                    </Form.Field>
                  </Form>
                ) : (
                  <Button color="blue" onClick={() => setShowForm(true)}>
                    Change configuration
                  </Button>
                )}
                {config?.is_configured && (
                  <>
                    <hr style={{ marginTop: "25px" }} />
                    <Header>Step 3: Add phone numbers</Header>
                    <p>
                      Once you add a phone number in the Meta developer portal
                      you will be able to add it to Connect.
                    </p>
                    {config?.active_numbers?.length > 0 && (
                      <>
                        <Header as="h3">Added to Connect</Header>
                        <List celled>
                          {config?.active_numbers?.map((number) => {
                            return (
                              <ListItem
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  padding: "0.5em",
                                }}
                              >
                                {number.phone_number
                                  ? `${number.phone_number} (ID: ${number.phone_number_id})`
                                  : `Phone number ID: ${number.phone_number_id}`}
                                {!number.is_verified &&
                                  !config?.warnings.length > 0 && (
                                    <Popup
                                      position="bottom center"
                                      content={"Pending WhatsApp verification"}
                                      trigger={
                                        <Icon
                                          size="small"
                                          name="hourglass half"
                                          style={{ margin: "0px 10px" }}
                                        />
                                      }
                                    />
                                  )}
                                {!number.in_whatsapp && (
                                  <Popup
                                    position="bottom center"
                                    content={
                                      "This number is active in Connect but we couldn't verify it with WhatsApp. Check that it's correctly set up, and that there are no warnings below about your WhatsApp configuration."
                                    }
                                    trigger={
                                      <Icon
                                        size="small"
                                        color="orange"
                                        name="exclamation triangle"
                                        style={{ margin: "0px 5px" }}
                                      />
                                    }
                                  />
                                )}
                              </ListItem>
                            );
                          })}
                        </List>
                      </>
                    )}
                    {config?.warnings?.length > 0 ? (
                      <Message negative>
                        <MessageHeader>
                          Unable to get your registered numbers from WhatsApp
                        </MessageHeader>
                        {config?.warnings.map((warning, index) => (
                          <p>{warning}</p>
                        ))}
                      </Message>
                    ) : (
                      config?.available_numbers?.length > 0 && (
                        <>
                          <Header as="h3">Ready to add</Header>
                          <List celled>
                            {config?.available_numbers?.map((number) => {
                              return (
                                <ListItem style={{ padding: "0.5em" }}>
                                  <Button
                                    inverted
                                    size="tiny"
                                    circular
                                    color="green"
                                    icon="plus circle"
                                    style={{ marginRight: "10px" }}
                                    onClick={() =>
                                      handleAddNumber(
                                        number.phone_number_id,
                                        number.phone_number,
                                      )
                                    }
                                  />
                                  {number.phone_number}
                                </ListItem>
                              );
                            })}
                          </List>
                        </>
                      )
                    )}
                  </>
                )}
              </>
            )}
          </Modal.Description>
        </Modal.Content>
        <Modal.Actions>
          <Button
            color="black"
            onClick={() => {
              setShowForm(false);
              setError("");
              setAppSecret("");
              setApiKey("");
              setWhatsappBusinessId("");
              setOpen(false);
            }}
            disabled={submitting}
          >
            {isFormModified() ? "Cancel" : "Close"}
          </Button>
          {isFormModified() && (
            <Button
              content="Save"
              labelPosition="right"
              icon="checkmark"
              loading={submitting}
              disabled={submitting}
              onClick={handleSubmit}
              positive
            />
          )}
        </Modal.Actions>
      </Modal>
    </>
  );
}

function Dynamics365Integration() {
  const { setApiUser, hasIntegration } = useAuth();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [contactsAppId, setContactsAppId] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [disconnecting, setDisconnecting] = useState(false);

  const handleSubmit = () => {
    setLoading(true);
    loginDynamics365({
      url: url,
      contacts_app_id: contactsAppId,
      tenant_id: tenantId,
      client_id: clientId,
      client_secret: clientSecret,
    })
      .then((res) => {
        toast("Successfully connected", { type: "success" });
        setUrl("");
        setContactsAppId("");
        setTenantId("");
        setClientId("");
        setClientSecret("");
        setApiUser(res.data.data);
        setOpen(false);
      })
      .catch((res) => setError(res.response.data.message))
      .finally(() => setLoading(false));
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const res = await disconnectService("dynamics365");
      setApiUser(res.data.data);
      toast("Successfully disconnected", { type: "success" });
    } catch (e) {
      toast("Failed to disconnect", { type: "error" });
    }
    setDisconnecting(false);
  };

  return (
    <>
      {hasIntegration(["dynamics365"]) ? (
        <Button
          primary
          onClick={handleDisconnect}
          disabled={disconnecting}
          loading={disconnecting}
        >
          Disconnect Dynamics
        </Button>
      ) : (
        <Modal
          onClose={() => setOpen(false)}
          onOpen={() => setOpen(true)}
          open={open}
          trigger={
            <Label
              image
              size="big"
              as="a"
              color="blue"
              style={{ paddingTop: "0.49em" }}
            >
              <img src={dynamics365Logo} alt="Dynamics365 logo" />
              Connect Dynamics
            </Label>
          }
        >
          <Modal.Header>Dynamics365 Integration</Modal.Header>
          <Modal.Content>
            <Modal.Description>
              <p>
                If you do not have the below information you can{" "}
                <a
                  href="https://docs.connect.cosoft.co.uk/apps/dynamics365"
                  target="_blank"
                  rel="noreferrer"
                >
                  follow the guide here to retrieve it
                </a>
                .
              </p>

              <Form>
                <Form.Field>
                  <input
                    placeholder="Enter Dynamics365 URL"
                    value={url}
                    onChange={(e) => {
                      setError("");
                      setUrl(e.target.value.trim());
                    }}
                  />
                </Form.Field>

                <Form.Field>
                  <input
                    placeholder="Enter Dynamics365 contacts app ID"
                    value={contactsAppId}
                    onChange={(e) => {
                      setError("");
                      setContactsAppId(e.target.value.trim());
                    }}
                  />
                </Form.Field>

                <Form.Field>
                  <input
                    placeholder="Enter Microsoft app registration tenant ID"
                    value={tenantId}
                    onChange={(e) => {
                      setError("");
                      setTenantId(e.target.value.trim());
                    }}
                  />
                </Form.Field>

                <Form.Field>
                  <input
                    placeholder="Enter Microsoft app registration client ID"
                    value={clientId}
                    onChange={(e) => {
                      setError("");
                      setClientId(e.target.value.trim());
                    }}
                  />
                </Form.Field>

                <Form.Field>
                  <input
                    placeholder="Enter Microsoft app registration client secret"
                    value={clientSecret}
                    type="password"
                    onChange={(e) => {
                      setError("");
                      setClientSecret(e.target.value.trim());
                    }}
                  />
                </Form.Field>

                {error.length > 0 && (
                  <Message negative>
                    <p>{error}</p>
                  </Message>
                )}
              </Form>
            </Modal.Description>
          </Modal.Content>
          <Modal.Actions>
            <Button
              color="black"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              content="Save"
              labelPosition="right"
              icon="checkmark"
              loading={loading}
              disabled={loading}
              onClick={handleSubmit}
              positive
            />
          </Modal.Actions>
        </Modal>
      )}
    </>
  );
}

export default Apps;
